/**
 * NotificationService - In-app notifications
 *
 * Manages user notifications for various events:
 * - Task deadlines
 * - Invoice due dates
 * - Unread messages
 * - Project milestones
 *
 * Future: Can be enhanced with push notifications, email digests, etc.
 */

export type NotificationType = 'info' | 'warning' | 'success' | 'error';
export type NotificationCategory = 'task' | 'project' | 'message' | 'invoice' | 'system';

export interface Notification {
  id: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  entityId?: string; // Related entity ID
  createdAt: string;
  read: boolean;
  actionLabel?: string;
  actionUrl?: string;
}

class NotificationServiceClass {
  private notifications: Notification[] = [];
  private listeners: Array<(notifications: Notification[]) => void> = [];

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Get all notifications
   */
  getAll(): Notification[] {
    return [...this.notifications];
  }

  /**
   * Get unread notifications
   */
  getUnread(): Notification[] {
    return this.notifications.filter(n => !n.read);
  }

  /**
   * Get unread count
   */
  getUnreadCount(): number {
    return this.getUnread().length;
  }

  /**
   * Add a new notification
   */
  add(notification: Omit<Notification, 'id' | 'createdAt' | 'read'>): Notification {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      read: false,
    };

    this.notifications.unshift(newNotification);

    // Keep only last 100 notifications
    if (this.notifications.length > 100) {
      this.notifications = this.notifications.slice(0, 100);
    }

    this.saveToStorage();
    this.notifyListeners();

    return newNotification;
  }

  /**
   * Mark notification as read
   */
  markAsRead(id: string): void {
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      notification.read = true;
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  /**
   * Mark all as read
   */
  markAllAsRead(): void {
    this.notifications.forEach(n => n.read = true);
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Delete notification
   */
  delete(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.notifications = [];
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Subscribe to notification changes
   */
  subscribe(listener: (notifications: Notification[]) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.getAll()));
  }

  /**
   * Save to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem('notifications', JSON.stringify(this.notifications));
    } catch (error) {
      console.error('Failed to save notifications:', error);
    }
  }

  /**
   * Load from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('notifications');
      if (stored) {
        this.notifications = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
      this.notifications = [];
    }
  }
}

// Singleton instance
export const NotificationService = new NotificationServiceClass();

// Helper functions for common notification types

export function notifyTaskDeadline(taskId: string, taskTitle: string, dueDate: string): void {
  NotificationService.add({
    type: 'warning',
    category: 'task',
    title: 'Task deadline',
    message: `"${taskTitle}" is due on ${new Date(dueDate).toLocaleDateString(undefined)}`,
    entityId: taskId,
    actionLabel: 'View',
    actionUrl: `/projects?task=${taskId}`,
  });
}

export function notifyInvoiceOverdue(invoiceId: string, invoiceNumber: string): void {
  NotificationService.add({
    type: 'error',
    category: 'invoice',
    title: 'Invoice overdue',
    message: `Invoice ${invoiceNumber} is overdue`,
    entityId: invoiceId,
    actionLabel: 'View',
    actionUrl: `/accounting/invoices/${invoiceId}`,
  });
}

export function notifyNewMessage(conversationId: string, sender: string): void {
  NotificationService.add({
    type: 'info',
    category: 'message',
    title: 'New message',
    message: `New message from ${sender}`,
    entityId: conversationId,
    actionLabel: 'Read',
    actionUrl: `/hub/conversations/${conversationId}`,
  });
}

export function notifyProjectMilestone(projectId: string, projectName: string, milestoneName: string): void {
  NotificationService.add({
    type: 'success',
    category: 'project',
    title: 'Milestone completed',
    message: `"${milestoneName}" in ${projectName}`,
    entityId: projectId,
    actionLabel: 'View',
    actionUrl: `/projects/${projectId}`,
  });
}
